import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, map, Observable, of, switchMap, take } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';
import { DistanceTimeInfo, DistanceTimeInfoRequest } from '../shared/map/map.component';
import { DistanceUnitByZoneId } from '../shared/pipes/DistanceUnitByZoneId.pipe';
import { LatLng } from '../pages/new-service/interfaces/LongitudeLatitude';
import { DistanceUnitByZoneIdService } from './distance-unit-by-zone-id.service';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsService {

  private geocoder = new google.maps.Geocoder();
  private placesService = new google.maps.places.PlacesService(document.createElement('div'));

  constructor(private http: HttpClient, private authService: AuthService, private distanceUnitByZoneId: DistanceUnitByZoneIdService) { }

  url = environment.products_url;

  // Obtener latitud y longitud de una dirección
  getLatLongFromAddress(address: string | undefined): Promise<LatLng> | undefined {
    if (!address) { return undefined }
    return new Promise((resolve, reject) => {
      this.geocoder.geocode({ 'address': address }, (results: any, status: any) => {
        if (status === google.maps.GeocoderStatus.OK) {
          const location = results[0].geometry.location;
          resolve({ lat: location.lat(), lng: location.lng() });
        } else {
          reject('Geocode was not successful for the following reason: ' + status);
        }
      });
    });
  }

  // Obtener dirección a partir de latitud y longitud
  getAddressFromLatLong(lat: number, lng: number): Promise<string> {
    const latLng = new google.maps.LatLng(lat, lng);
    return new Promise((resolve, reject) => {
      this.geocoder.geocode({ 'location': latLng }, (results: any, status: any) => {
        if (status === google.maps.GeocoderStatus.OK) {
          resolve(results[0].formatted_address);
        } else {
          reject('Reverse geocode was not successful for the following reason: ' + status);
        }
      });
    });
  }

  // Autocompletar direcciones
  getAutocompletePredictions(input: string): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const autocompleteService = new google.maps.places.AutocompleteService();
      autocompleteService.getPlacePredictions({ input }, (predictions: any, status: any) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          resolve(predictions);
        } else {
          reject('Autocomplete was not successful for the following reason: ' + status);
        }
      });
    });
  }

  getDistance(originLatitude: number | null | undefined, originLongitude: number | null | undefined, destinationLatitude: number | null | undefined, destinationLongitude: number | null | undefined): Observable<DistanceTimeInfoRequest> {
    return this.http.get<DistanceTimeInfoRequest>(`${this.url}/route-information?originLatitude=${originLatitude}&originLongitude=${originLongitude}&destinationLatitude=${destinationLatitude}&destinationLongitude=${destinationLongitude}`, {})
  }

  getPlaceDetails(placeId: string): Promise<google.maps.places.PlaceResult> {
    const service = new google.maps.places.PlacesService(document.createElement('div'));

    return new Promise((resolve, reject) => {
      service.getDetails({ placeId }, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          resolve(place);
        } else {
          reject(`Place details request failed with status: ${status}`);
        }
      });
    });
  }

  getFormattedTravelInfo(origin: LatLng, destination: LatLng): Observable<DistanceTimeInfo | null> {
    if (!origin || !destination) return of(null);

    return this.getDistance(origin.lat, origin.lng, destination.lat, destination.lng).pipe(
      take(1),
      map((data: DistanceTimeInfoRequest) => {
        if (data && data.Distance && data.Time) {
          return {
            Distance: `${data.Distance}${this.distanceUnitByZoneId.transform(data.ZoneId)}`,
            Time: `${Math.round(data.Time)} min. Aprox.`
          };
        }
        return null;
      })
    );
  }

}
